// TypeFieldsFieldList.jsx

import React from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// Components
import DatabaseSettingsAddButton from 'collection/settings/addbutton/DatabaseSettingsAddButton';
import TypeFieldsFieldRow from 'collection/typefields/fieldrow/TypeFieldsFieldRow';
import TypeFieldsTitleField from 'collection/typefields/titlefield/TypeFieldsTitleField';

/**
 * TypeFieldsFieldList
 *
 * Renders insert controls and non-title field rows for a type, including empty state.
 *
 * @param {array} fields - Non-title fields to render.
 * @param {string} draggingFieldName - Currently dragged field name.
 * @param {string} dragOverFieldName - Current drag-over target field name.
 * @param {boolean} isSystemType - Whether type editing is disabled for system types.
 * @param {function} onAddFieldClick - Handler to open insert flow at a given index.
 * @param {function} onDragEnd - Handler for drag end.
 * @param {function} onDragOver - Handler for drag over.
 * @param {function} onDragStart - Handler for drag start.
 * @param {function} onDrop - Handler for drop.
 * @param {function} onSettingsClick - Handler for opening field settings.
 *
 * @returns {ReactNode} The rendered field list section.
 */
const TypeFieldsFieldList = React.memo(({
  fields,
  draggingFieldName,
  dragOverFieldName,
  isSystemType,
  onAddFieldClick,
  onDragEnd,
  onDragOver,
  onDragStart,
  onDrop,
  onSettingsClick,
}) => {

  // ----- CONTEXT ----- //

  // Theme Context

  const { theme } = useTheme();

  // ----- RENDER ----- //

  return (
    <>
      <DatabaseSettingsAddButton
        title={isSystemType ? 'System types cannot be edited.' : 'Add Field'}
        disabled={isSystemType}
        onClick={() => onAddFieldClick(0)}
      />

      {/* TITLE FIELD (IMMUTABLE) */}
      <TypeFieldsTitleField />

      {fields.map((field) => {

        const isDragging = draggingFieldName === field.name;
        const isDropTarget = dragOverFieldName === field.name;

        return (
          <TypeFieldsFieldRow
            key={field.name}
            field={field}
            isDragging={isDragging}
            isDropTarget={isDropTarget}
            isSystemType={isSystemType}
            onDragEnd={onDragEnd}
            onDragOver={(event) => onDragOver(field, event)}
            onDragStart={(event) => onDragStart(field, event)}
            onDrop={(event) => onDrop(field, event)}
            onSettingsClick={() => onSettingsClick(field)}
          />
        );
      })}

      {fields.length > 0 && (
        <DatabaseSettingsAddButton
          title={isSystemType ? 'System types cannot be edited.' : 'Add Field'}
          disabled={isSystemType}
          onClick={() => onAddFieldClick(fields.length)}
        />
      )}

      {fields.length === 0 && (
        <div
          className="type-fields-empty"
          style={{
            color: theme.foregroundMuted,
          }}
        >
          No fields yet. Add one to get started.
        </div>
      )}
    </>
  );
});

// Dev Tools
TypeFieldsFieldList.displayName = 'TypeFieldsFieldList';

export default TypeFieldsFieldList;
